import { useEffect, useRef, useState } from 'react';
import type { PlacePrediction } from '@bilmat/shared';
import { apiGet, ApiError } from '../lib/apiClient';

const DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 2;

function randomSessionToken(): string {
  const rand = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(rand, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export type AutocompleteState = {
  predictions: PlacePrediction[];
  isLoading: boolean;
  error: string | null;
};

export type UseAutocomplete = AutocompleteState & {
  newSession: () => void;
};

export function useAutocomplete(query: string): UseAutocomplete {
  const [state, setState] = useState<AutocompleteState>({
    predictions: [],
    isLoading: false,
    error: null,
  });
  const sessionRef = useRef(randomSessionToken());

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setState({ predictions: [], isLoading: false, error: null });
      return;
    }

    const controller = new AbortController();
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    const timer = window.setTimeout(() => {
      void apiGet<{ predictions: PlacePrediction[] }>(
        'autocomplete',
        { input: trimmed, sessiontoken: sessionRef.current },
        controller.signal,
      )
        .then((data) => {
          setState({ predictions: data.predictions, isLoading: false, error: null });
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          const message =
            err instanceof ApiError ? err.code : 'Kunde inte hämta förslag just nu.';
          setState({ predictions: [], isLoading: false, error: message });
        });
    }, DEBOUNCE_MS);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [query]);

  const newSession = (): void => {
    sessionRef.current = randomSessionToken();
  };

  return { ...state, newSession };
}
